import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import VideoPlayer from '../components/VideoPlayer';
import { api } from '../api/api';
import { toast } from 'sonner';

const Offer4 = ({ userId }) => {
  const navigate = useNavigate();
  const [variant, setVariant] = useState(null);
  const [loading, setLoading] = useState(true);

  const prices = { A: 9, B: 15 };

  useEffect(() => {
    const fetchVariant = async () => {
      try {
        const result = await api.getABVariant('offer4', userId);
        setVariant(result.variant);
      } catch (error) {
        console.error('Error fetching variant:', error);
        setVariant('A');
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchVariant();
    }
  }, [userId]);

  const handleAccept = async () => {
    try {
      const price = prices[variant];
      await api.addToCart({
        userId,
        productId: 'digital-course',
        productName: 'Learn Luganda Language Basics (Digital Course)',
        price,
        variant: variant
      });
      
      toast.success('✓ Language Course added to cart!');
      
      setTimeout(() => {
        navigate('/cart');
      }, 1000);
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  const handleDecline = async () => {
    try {
      await api.enrollInSequence({ userId, sequenceType: 'sequence4' });
      navigate('/cart');
    } catch (error) {
      console.error('Error enrolling in sequence:', error);
      navigate('/cart');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading offer...</p>
        </div>
      </div>
    );
  }

  const price = prices[variant];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-block px-4 py-2 bg-teal-100 text-teal-800 rounded-full text-sm font-semibold mb-4">
              Special Offer #4
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{color: '#3D2817'}}>
              Learn Luganda Language Basics
            </h2>
            <p className="text-xl text-gray-700">
              Digital short course (PDF with audio)
            </p>
          </div>

          <div className="mb-8">
            <VideoPlayer videoUrl="" />
          </div>

          <div className="bg-white rounded-lg shadow-xl p-8 mb-8">
            <h3 className="text-2xl font-bold mb-6" style={{color: '#C85A3E'}}>
              Master Essential Luganda Phrases:
            </h3>
            
            <div className="space-y-4 mb-8">
              <div className="flex items-start">
                <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-gray-700"><strong>100+ essential phrases</strong> for everyday conversations</p>
              </div>
              <div className="flex items-start">
                <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-gray-700"><strong>Audio pronunciations</strong> from native speakers</p>
              </div>
              <div className="flex items-start">
                <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-gray-700"><strong>Cultural context</strong> to use phrases appropriately</p>
              </div>
              <div className="flex items-start">
                <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-gray-700"><strong>Printable phrasebook</strong> for easy reference</p>
              </div>
              <div className="flex items-start">
                <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-gray-700"><strong>Practice exercises</strong> to reinforce learning</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-teal-50 to-purple-50 rounded-lg p-6 text-center mb-8">
              <p className="text-gray-600 mb-2">Introductory Offer</p>
              <div className="text-5xl font-bold mb-2" style={{color: '#2C7A9F'}}>
                ${price}
              </div>
              <p className="text-sm text-gray-600">Lifetime access • Digital course</p>
            </div>

            <div className="space-y-4">
              <button
                onClick={handleAccept}
                className="w-full py-4 rounded-lg font-bold text-white text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition"
                style={{backgroundColor: '#2C7A9F'}}
                data-testid="accept-offer-4"
              >
                Yes I want the course
              </button>
              
              <button
                onClick={handleDecline}
                className="w-full py-3 rounded-lg font-medium text-gray-600 border-2 border-gray-300 hover:border-gray-400 transition"
                data-testid="decline-offer-4"
              >
                No Thanks
              </button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Offer4;